export const FALLBACK_HANDLE = "FALLBACK";
export const LINEAR_HANDLE = "next";
export const CONDITION_TRUE_HANDLE = "onTrue";
export const CONDITION_FALSE_HANDLE = "onFalse";
export const ACTION_SUCCESS_HANDLE = "onSuccess";
export const ACTION_FAILURE_HANDLE = "onFailure";
export const COLLECT_RECEIVED_HANDLE = "onReceived";
export const INVOKE_COMPLETED_HANDLE = "onCompleted";
export const TIMEOUT_HANDLE = "onTimeout";

export const PALETTE = {
  COLLECT_INPUT: "generic.collect_input",
  CONDITION: "generic.condition",
  ACTION: "generic.action",
  HANDOFF: "generic.handoff",
  END: "generic.end",
  INVOKE_WORKFLOW: "generic.invoke_workflow",
  TRANSFER_WORKFLOW: "generic.transfer_workflow"
};

const MESSAGE_WORKFLOW_TYPES = new Set(["SEND_MESSAGE", "NOTIFY_USER", "AWAIT_USER_REPLY"]);

const GENERIC_PALETTE_TYPES = new Set(Object.values(PALETTE));

export function isWhatsappPalette(paletteType) {
  return typeof paletteType === "string" && paletteType.startsWith("whatsapp.");
}

export function isNotifyPalette(paletteType) {
  return isWhatsappPalette(paletteType) && paletteType.endsWith(".notify");
}

export function isAwaitPalette(paletteType) {
  return isWhatsappPalette(paletteType) && paletteType.endsWith(".await");
}

export function isMapTransitionPalette(paletteType, config = {}) {
  if (!isWhatsappPalette(paletteType)) return false;
  if (paletteType === "whatsapp.await") {
    const msgType = config?.message?.type;
    return msgType === "buttons" || msgType === "list";
  }
  return /\.buttons(\.|$)/.test(paletteType) || /\.list(\.|$)/.test(paletteType);
}

export function isLinearMessagePalette(paletteType, config = {}) {
  return isWhatsappPalette(paletteType) && !isMapTransitionPalette(paletteType, config) && !isNotifyPalette(paletteType);
}

export function usesOnSuccessRef(workflowType) {
  return workflowType === "NOTIFY_USER";
}

export function usesTransitions(workflowType) {
  return workflowType === "SEND_MESSAGE" || workflowType === "AWAIT_USER_REPLY";
}

export function supportsTimeoutRef(workflowType) {
  return (
    workflowType === "AWAIT_USER_REPLY" ||
    workflowType === "SEND_MESSAGE" ||
    workflowType === "COLLECT_INPUT"
  );
}

export function isMessageWorkflowType(workflowType) {
  return MESSAGE_WORKFLOW_TYPES.has(workflowType);
}

export function hasSourceHandles(paletteType) {
  return paletteType !== PALETTE.END;
}

function resolveMessagePaletteBase(messageType, message = {}) {
  if (messageType === "template") {
    return message.headerImageLink || message.headerImageMediaId ? "template_media" : "template_text";
  }
  if (messageType === "buttons") return "buttons";
  if (messageType === "list") return "list";
  return messageType || "text";
}

const PALETTE_SEGMENT_TO_MESSAGE_TYPE = {
  text: "text",
  image: "image",
  video: "video",
  document: "document",
  buttons: "buttons",
  list: "list",
  template_text: "template",
  template_media: "template",
};

/** WhatsApp message `type` implied by a palette node (e.g. whatsapp.image.notify → image). */
export function getMessageTypeFromPalette(paletteType) {
  if (!isWhatsappPalette(paletteType)) return null;
  const segment = paletteType.replace(/^whatsapp\./, "").split(".")[0];
  return PALETTE_SEGMENT_TO_MESSAGE_TYPE[segment] ?? null;
}

/** Palette entry already defines message format — hide generic Message Type selector. */
export function hasFixedMessageTypePalette(paletteType) {
  return getMessageTypeFromPalette(paletteType) != null;
}

function buildWhatsappPaletteType(runtimeType, messageType, message = {}) {
  const base = resolveMessagePaletteBase(messageType, message);
  if (runtimeType === "NOTIFY_USER") return `whatsapp.${base}.notify`;
  if (runtimeType === "AWAIT_USER_REPLY") return `whatsapp.${base}.await`;
  return `whatsapp.${base}`;
}

const TYPE_TO_PALETTE = {
  COLLECT_INPUT: PALETTE.COLLECT_INPUT,
  CONDITION: PALETTE.CONDITION,
  HANDOFF: PALETTE.HANDOFF,
  END: PALETTE.END,
  INVOKE_WORKFLOW: PALETTE.INVOKE_WORKFLOW,
  TRANSFER_WORKFLOW: PALETTE.TRANSFER_WORKFLOW,
  ACTION: PALETTE.ACTION
};

export function inferPaletteTypeFromLegacyNode(node) {
  if (node.paletteType) return node.paletteType;

  const runtimeType = node.type;
  const configAction = node.config?.action;

  if (runtimeType === "ACTION" && typeof configAction === "string" && configAction.startsWith("CUSTOM:")) {
    return `custom.${configAction.replace(/^CUSTOM:/, "")}`;
  }

  if (MESSAGE_WORKFLOW_TYPES.has(runtimeType)) {
    if (runtimeType === "NOTIFY_USER") return "whatsapp.notify";
    if (runtimeType === "AWAIT_USER_REPLY") return "whatsapp.await";
    const message = node.config?.message || {};
    return buildWhatsappPaletteType(runtimeType, message.type, message);
  }

  return TYPE_TO_PALETTE[runtimeType] ?? null;
}

/** Resolve engine type from API node (type field or paletteType). */
export function inferWorkflowTypeFromNode(node) {
  if (node.type) return node.type;

  const paletteType = node.paletteType || inferPaletteTypeFromLegacyNode(node);
  if (!paletteType) return null;

  if (isNotifyPalette(paletteType)) return "NOTIFY_USER";
  if (isAwaitPalette(paletteType)) return "AWAIT_USER_REPLY";
  if (isWhatsappPalette(paletteType)) return "SEND_MESSAGE";

  const paletteToType = Object.fromEntries(
    Object.entries(TYPE_TO_PALETTE).map(([type, palette]) => [palette, type])
  );
  if (paletteType.startsWith("custom.")) return "ACTION";
  return paletteToType[paletteType] ?? null;
}

export function isPaletteDrivenNode(paletteType) {
  return isWhatsappPalette(paletteType);
}

export function paletteToCanvasType(paletteType) {
  if (isWhatsappPalette(paletteType)) return "message";
  if (paletteType?.startsWith("custom.")) return "action";
  if (paletteType === PALETTE.CONDITION) return "condition";
  if (paletteType === PALETTE.COLLECT_INPUT) return "trigger";
  if (GENERIC_PALETTE_TYPES.has(paletteType) && paletteType !== PALETTE.COLLECT_INPUT && paletteType !== PALETTE.CONDITION) {
    return "action";
  }
  return "trigger";
}

function getMessagePreviewText(message = {}) {
  switch (message.type) {
    case "text":
    case "buttons":
    case "list":
      return typeof message.text === "string" ? message.text : "";
    case "image":
    case "video":
    case "document":
      if (typeof message.caption === "string" && message.caption) return message.caption;
      if (typeof message.url === "string" && message.url) return message.url;
      if (typeof message.mediaId === "string" && message.mediaId) return message.mediaId;
      if (typeof message.filename === "string" && message.filename) return message.filename;
      return "";
    case "template":
      return message.name || message.templateName || (message.templateId ? String(message.templateId) : "");
    default:
      return typeof message.text === "string" ? message.text : "";
  }
}

/** Canvas preview text derived from GET_WORKFLOW node shapes. */
export function getNodeDescription(node) {
  const config = node.config || {};
  const workflowType = inferWorkflowTypeFromNode(node);

  if (isMessageWorkflowType(workflowType) || isWhatsappPalette(node.paletteType)) {
    return getMessagePreviewText(config.message);
  }

  switch (workflowType) {
    case "COLLECT_INPUT":
      return typeof config.prompt === "string" ? config.prompt : "";
    case "HANDOFF":
      return typeof config.messageToUser === "string" ? config.messageToUser : "";
    case "ACTION":
      return typeof node.action === "string" ? node.action : typeof config.action === "string" ? config.action : "";
    case "INVOKE_WORKFLOW":
    case "TRANSFER_WORKFLOW":
      return typeof config.workflowId === "string" ? config.workflowId : "";
    case "CONDITION": {
      const conditions = node.conditions ?? config.conditions;
      return Array.isArray(conditions) && conditions.length ? `${conditions.length} condition(s)` : "";
    }
    case "END":
      return "";
    default:
      return "";
  }
}

/** Live preview for canvas nodes from current config (not stale data.description). */
export function getCanvasNodePreviewText(data) {
  if (!data) return "";

  const derived = getNodeDescription({
    type: data.workflowType,
    paletteType: data.paletteType,
    config: data.config,
    action: data.config?.action,
    conditions: data.conditions
  });

  if (derived && !String(derived).startsWith("CUSTOM:")) return derived;
  if (typeof data.label === "string" && data.label) return data.label;
  return typeof data.description === "string" ? data.description : "";
}
